import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import LoadingSpinner from './LoadingSpinner';
import { logout } from '../api/api';

const ProtectedRoute = ({
  children,
  requiredPermission,
  requiredRole,
  allRoutes,
  fallbackPath = '/auth/sign-in',
  showToast = true
}) => {
  const { hasPermission, hasRole, loading, user } = usePermissions();
  const location = useLocation();
  const [sessionCleared, setSessionCleared] = useState(false);
  const clearSessionStartedRef = useRef(false);

  // Vérifier les permissions
  let hasAccess = true;

  // Vérifier la permission spécifique
  if (requiredPermission && !hasPermission(requiredPermission)) {
    hasAccess = false;
  }

  // Vérifier le rôle spécifique
  if (requiredRole && !hasRole(requiredRole)) {
    hasAccess = false;
  }

  // Fonction pour trouver la première route autorisée
  const getFirstAuthorizedRoute = (routesList) => {
    for (const route of routesList) {
      if (route.category && route.items) {
        const found = getFirstAuthorizedRoute(route.items);
        if (found) return found;
      } else if (route.items) {
        const found = getFirstAuthorizedRoute(route.items);
        if (found) return found;
      } else if (route.path && !route.hidden && route.layout !== '/auth') {
        const permission = route.requiredPermission;
        if (!permission || hasPermission(permission)) {
          return `${route.layout}${route.path}`;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (!loading && !hasAccess && user && !clearSessionStartedRef.current) {
      clearSessionStartedRef.current = true;
      (async () => {
        try {
          await logout();
        } finally {
          setSessionCleared(true);
        }
      })();
    }
  }, [loading, hasAccess, user]);

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <LoadingSpinner />
    );
  }

  // Si pas d'accès, rediriger et afficher un message
  if (!hasAccess) {
    if (!user) {
      return <Navigate to={fallbackPath} state={{ from: location }} replace />;
    }

    // Si on est déjà connecté (user existe), on redirige vers la première page autorisée
    if (user && allRoutes) {
      const firstRoute = getFirstAuthorizedRoute(allRoutes);
      if (firstRoute && location.pathname !== firstRoute) {
        return <Navigate to={firstRoute} replace />;
      }
    }

    if (!sessionCleared) {
      return <LoadingSpinner />;
    }

    return (
      <Navigate
        to={fallbackPath}
        state={{
          from: location,
          accessDenied: true,
          accessDeniedKey: `access-denied-${Date.now()}`,
          showAccessDeniedToast: showToast,
        }}
        replace
      />
    );
  }

  return children;
};

export default ProtectedRoute;

